/**
 * Batch H2 — Holder dashboard overview. Claims/entitlements are account-based and shown
 * separately from verified on-chain NFT ownership; unminted claims are never shown as NFTs.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Gift, ShieldCheck, Layers, Unlock, Clock } from 'lucide-react';
import theme from '../../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stat({ Icon, label, value, hint, to, testid }) {
  const navigate = useNavigate();
  return (
    <button data-testid={testid} onClick={() => navigate(to)} style={{ textAlign: 'left', padding: 16, borderRadius: 12, border: `1px solid ${theme.colors.borders}`, background: '#fff', cursor: 'pointer' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: theme.colors.text.muted, fontSize: 12 }}><Icon size={14} /> {label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: theme.colors.text.primary, marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 11, color: theme.colors.text.muted, marginTop: 2 }}>{hint}</div>
    </button>
  );
}

export default function HolderOverview() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`${API}/buyer/overview`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => setD(r.data)).catch(e => setErr(e?.response?.data?.detail || 'Could not load your overview'));
  }, []);
  if (err) return <div data-testid="holder-overview-error" style={{ padding: 12, borderRadius: 8, background: '#FEF2F2', color: '#991B1B', marginBottom: 20 }}>{err}</div>;
  if (!d) return <div data-testid="holder-overview-loading" style={{ marginBottom: 20, color: theme.colors.text.muted }}>Loading your Kickbacks…</div>;
  const b = d.benefits || {};
  return (
    <div data-testid="holder-overview" style={{ marginBottom: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
        <Stat testid="holder-stat-claimed" Icon={Gift} label="Claimed Kickbacks" value={d.claimed_kickbacks} hint={`${d.active_entitlements} active entitlement(s)`} to="/buyer/claims" />
        <Stat testid="holder-stat-available" Icon={Unlock} label="Benefits available" value={b.available || 0} hint={`${b.redeemed || 0} redeemed`} to="/buyer/utilities" />
        <Stat testid="holder-stat-locked" Icon={Clock} label="Locked or upcoming" value={(b.locked || 0) + (b.scheduled || 0)} hint={`${b.scheduled || 0} scheduled`} to="/buyer/utilities" />
        <Stat testid="holder-stat-nfts" Icon={Layers} label="Verified NFTs" value={d.verified_nfts} hint="Confirmed on-chain in verified wallets" to="/buyer/settings" />
        <Stat testid="holder-stat-wallets" Icon={ShieldCheck} label="Wallets (optional)" value={`${d.wallets_verified}/${d.wallets_saved}`} hint="verified / saved" to="/buyer/settings" />
      </div>
      {d.nfts_awaiting_issuance > 0 && (
        <div data-testid="holder-issuance-note" style={{ marginTop: 10, fontSize: 12, color: '#92400E' }}>
          {d.nfts_awaiting_issuance} claim(s) have an entitlement but no NFT issued yet — NFT issuance for claims is not available yet. Your account benefits still work.
        </div>
      )}
      <div style={{ marginTop: 16, background: '#fff', border: `1px solid ${theme.colors.borders}`, borderRadius: 12, padding: 16 }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Recent claims</div>
        {d.recent_claims.length === 0 ? (
          <div data-testid="holder-recent-empty" style={{ fontSize: 13, color: theme.colors.text.muted }}>
            No claims yet. When a creator shares a claim link or QR code, open it or paste it in My Claims.
            <button data-testid="holder-recent-go-claims" onClick={() => navigate('/buyer/claims')} style={{ marginLeft: 8, background: 'none', border: 'none', color: theme.colors.primary, fontWeight: 700, cursor: 'pointer' }}>Go to My Claims</button>
          </div>
        ) : d.recent_claims.map(c => (
          <button key={c.redemption_id} data-testid={`holder-recent-${c.redemption_id}`} onClick={() => navigate(`/buyer/claims/${c.redemption_id}`)}
            style={{ display: 'flex', width: '100%', gap: 10, alignItems: 'center', padding: '8px 0', borderTop: `1px solid ${theme.colors.borders}`, background: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: 'none', cursor: 'pointer', textAlign: 'left' }}>
            <span style={{ flex: 1, fontSize: 13 }}><b>{c.title || 'Kickback claim'}</b> · {c.portfolio_name}</span>
            <span style={{ fontSize: 11, color: '#92400E' }}>NFT: {c.issuance_label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
